import { faArrowUpRightFromSquare } from '@fortawesome/duotone-regular-svg-icons';
import { type IconProp } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { type FC, type HTMLInputTypeAttribute } from 'react';
import type { ZodError } from 'zod';
import { useFieldContext } from './contexts';
import { Field } from './Field';

interface Props {
  readonly isAutoFocus?: boolean;
  readonly isDisabled?: boolean;
  readonly isLinkButton?: boolean;
  readonly isLoading?: boolean;
  readonly isReadOnly?: boolean;
  readonly isRequired?: boolean;
  readonly isStatic?: boolean;
  readonly label: string;
  readonly leftIcon?: IconProp;
  readonly placeholder: string;
  readonly type?: HTMLInputTypeAttribute;
}

export const InputField: FC<Props> = ({
  isAutoFocus,
  isDisabled,
  isLinkButton,
  isLoading,
  isReadOnly,
  isRequired,
  isStatic,
  label,
  leftIcon,
  placeholder,
  type,
}) => {
  const field = useFieldContext<string>();
  return (
    <Field name={field.name} label={label} hasAddons={isLinkButton}>
      <div
        className={`control ${leftIcon ? 'has-icons-left' : ''} ${isLoading ? 'is-loading' : ''} ${isLinkButton ? 'is-expanded' : ''}`}
      >
        <input
          aria-disabled={isDisabled ?? false}
          disabled={isDisabled ?? false}
          autoFocus={isAutoFocus}
          className={`input ${field.state.meta.errors.length > 0 ? 'is-danger' : ''} ${isStatic ? 'is-static' : ''}`}
          placeholder={placeholder}
          aria-required={isRequired ?? false}
          required={isRequired ?? false}
          aria-readonly={isDisabled ?? isReadOnly ?? isStatic ?? false}
          readOnly={isDisabled ?? isReadOnly ?? isStatic ?? false}
          id={field.name}
          name={field.name}
          type={type ?? 'text'}
          value={field.state.value}
          onBlur={field.handleBlur}
          onChange={(e) => {
            field.handleChange(e.target.value);
          }}
        />
        {leftIcon ? (
          <span className={'icon is-small is-left'}>
            <FontAwesomeIcon icon={leftIcon} />
          </span>
        ) : null}
      </div>
      {isLinkButton ? (
        <div className={'control'}>
          <a className={'button'} title={'Open in new tab'} href={field.state.value} target={'_blank'}>
            <span className={'icon'}>
              <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
            </span>
          </a>
        </div>
      ) : null}
      {field.state.meta.errors.map((error: ZodError, idx) => (
        <p key={idx} className={'help is-danger'}>
          {error.message}
        </p>
      ))}
    </Field>
  );
};
InputField.displayName = 'InputField';
